import { inject, provide } from 'vue';
import type { InjectionKey } from 'vue';
import type { IEquipmentServiceClient } from './interfaces/IEquipmentServiceClient';
import type { ISystemDesignServiceClient } from './interfaces/ISystemDesignServiceClient';
import { EquipmentServiceClient } from './clients/EquipmentServiceClient';
import { FakeEquipmentServiceClient } from './clients/FakeEquipmentServiceClient';
import { SystemDesignServiceClient } from './clients/SystemDesignServiceClient';

export interface IAppContext {
    systemDesignService: ISystemDesignServiceClient;
    equipmentService: IEquipmentServiceClient;
}

export const APP_CONTEXT_KEY = Symbol('app-context') as InjectionKey<IAppContext>;

/** The equipment catalog when EQUIPMENT_SERVICE_DOMAIN names it, else a local stand-in: a laptop stays offline. */
const equipmentService = (): IEquipmentServiceClient =>
    useRuntimeConfig().public.EQUIPMENT_SERVICE_DOMAIN
        ? new EquipmentServiceClient()
        : new FakeEquipmentServiceClient();

export function createAppContext(overrides?: Partial<IAppContext>): IAppContext {
    return {
        systemDesignService: overrides?.systemDesignService ?? new SystemDesignServiceClient(),
        equipmentService: overrides?.equipmentService ?? equipmentService(),
    };
}

export function useAppContext(): IAppContext {
    const context = inject(APP_CONTEXT_KEY);
    if (!context) {
        throw new Error('App context not provided. Make sure to call provideAppContext() in your app setup.');
    }
    return context;
}

export function provideAppContext(context?: Partial<IAppContext>): void {
    const appContext = createAppContext(context);
    provide(APP_CONTEXT_KEY, appContext);
}
